import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router';
import { DateFilterEnum } from '@/ui';
import { TaskFilterContext } from './context';
import { useTimeSettings } from '@/common/hooks';

type TaskFilterProviderProps = {
    readonly children?: ReactNode;
};
export function TaskFilterProvider(
    props: TaskFilterProviderProps
): ReactElement {
    const { children } = props;

    const { timezone } = useTimeSettings();

    const [searchParams, setSearchParams] = useSearchParams();

    const [teamIds, _setTeamIds] = useState<Set<string>>(
        new Set(searchParams.getAll('tid'))
    );
    const [userIds, _setUserIds] = useState<Set<string>>(
        new Set(searchParams.getAll('uid'))
    );
    const [deviceIds, _setDeviceIds] = useState<Set<string>>(
        new Set(searchParams.getAll('did'))
    );
    const [selectedTaskId, _setSelectedTaskId] = useState<string | null>(
        searchParams.get('stid') ?? null
    );
    const [time, _setTime] = useState<string | null>(
        searchParams.get('t') ?? null
    );

    const setTeamIds = useCallback(
        (teamIds: string[]) => {
            setSearchParams((prev) => {
                prev.delete('tid');
                teamIds.forEach((id) => prev.append('tid', id));
                return prev;
            });
            _setTeamIds(new Set(teamIds));
        },
        [setSearchParams]
    );

    const setUserIds = useCallback(
        (userIds: string[]) => {
            setSearchParams((prev) => {
                prev.delete('uid');
                userIds.forEach((id) => prev.append('uid', id));
                return prev;
            });
            _setUserIds(new Set(userIds));
        },
        [setSearchParams]
    );

    const setDeviceIds = useCallback(
        (deviceIds: string[]) => {
            setSearchParams((prev) => {
                prev.delete('did');
                deviceIds.forEach((id) => prev.append('did', id));
                return prev;
            });
            _setDeviceIds(new Set(deviceIds));
        },
        [setSearchParams]
    );

    const setSelectedTaskId = useCallback(
        (taskId: string | null) => {
            setSearchParams((prev) => {
                prev.delete('stid');
                if (taskId) {
                    prev.append('stid', taskId);
                }
                return prev;
            });
            _setSelectedTaskId(taskId);
        },
        [setSearchParams]
    );

    const setTime = useCallback(
        (type: DateFilterEnum, range?: { from: Date; to: Date } | null) => {
            let time: string;
            switch (type) {
                case DateFilterEnum.Today:
                    time = '1d';
                    break;
                case DateFilterEnum.Last7Days:
                    time = '7d';
                    break;
                case DateFilterEnum.Last4Weeks:
                    time = '4w';
                    break;
                case DateFilterEnum.Last3Months:
                    time = '3m';
                    break;
                case DateFilterEnum.Last6Months:
                    time = '6m';
                    break;
                case DateFilterEnum.Last12Months:
                    time = '12m';
                    break;
                case DateFilterEnum.MonthToDate:
                    time = 'm';
                    break;
                case DateFilterEnum.YearToDate:
                    time = 'y';
                    break;
                case DateFilterEnum.Custom:
                    if (range) {
                        const fromDt = DateTime.fromJSDate(range.from).setZone(
                            timezone
                        );
                        const toDt = DateTime.fromJSDate(range.to).setZone(
                            timezone
                        );
                        const fromStr = fromDt.toFormat('yyyy-MM-dd');
                        const toStr = toDt.toFormat('yyyy-MM-dd');
                        time = `from_${fromStr}_to_${toStr}`;
                    } else {
                        const today = DateTime.now().setZone(timezone);
                        const from = today.minus({ days: 7 });
                        const fromStr = from.toFormat('yyyy-MM-dd');
                        const toStr = today.toFormat('yyyy-MM-dd');
                        time = `from_${fromStr}_to_${toStr}`;
                    }
                    break;
                case DateFilterEnum.AllTime:
                default:
                    time = 'all';
                    break;
            }
            setSearchParams((prev) => {
                prev.delete('t');
                prev.append('t', time);
                return prev;
            });
            _setTime(time);
        },
        [setSearchParams, timezone]
    );

    const onTeamFilterApplied = useCallback(
        (teamIds: string[]) => {
            setTeamIds(teamIds);
            // A different team scope invalidates any user/device selection.
            setUserIds([]);
            setDeviceIds([]);
        },
        [setTeamIds, setUserIds, setDeviceIds]
    );

    const [timeType, timeRange] = useMemo<
        [DateFilterEnum, { from: Date; to: Date } | null]
    >(() => {
        const today = DateTime.now().setZone(timezone).startOf('day');

        if (time === '1d') {
            const todayDate = today.toJSDate();
            return [DateFilterEnum.Today, { from: todayDate, to: todayDate }];
        }
        if (time === '7d') {
            return [
                DateFilterEnum.Last7Days,
                {
                    from: today.minus({ days: 7 }).toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === '4w') {
            return [
                DateFilterEnum.Last4Weeks,
                {
                    from: today.minus({ days: 7 * 4 }).toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === '3m') {
            return [
                DateFilterEnum.Last3Months,
                {
                    from: today.minus({ months: 3 }).toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === '6m') {
            return [
                DateFilterEnum.Last6Months,
                {
                    from: today.minus({ months: 6 }).toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === '12m') {
            return [
                DateFilterEnum.Last12Months,
                {
                    from: today.minus({ months: 12 }).toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === 'm') {
            return [
                DateFilterEnum.MonthToDate,
                {
                    from: today.startOf('month').toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === 'y') {
            return [
                DateFilterEnum.YearToDate,
                {
                    from: today.startOf('year').toJSDate(),
                    to: today.toJSDate()
                }
            ];
        }
        if (time === 'all') {
            return [DateFilterEnum.AllTime, null];
        }

        const dateRangePattern =
            /from_(\d{4}-\d{2}-\d{2})_to_(\d{4}-\d{2}-\d{2})/;

        if (time && dateRangePattern.test(time)) {
            const [, from, to] = time.match(dateRangePattern)!;
            const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
            const [toYear, toMonth, toDay] = to.split('-').map(Number);

            const fromDate = DateTime.fromObject(
                { year: fromYear, month: fromMonth, day: fromDay },
                { zone: timezone }
            ).toJSDate();
            const toDate = DateTime.fromObject(
                { year: toYear, month: toMonth, day: toDay },
                { zone: timezone }
            ).toJSDate();

            if (fromDate.getTime() > toDate.getTime()) {
                return [DateFilterEnum.Custom, { from: toDate, to: fromDate }];
            }
            return [DateFilterEnum.Custom, { from: fromDate, to: toDate }];
        }

        return [DateFilterEnum.AllTime, null];
    }, [time, timezone]);

    return (
        <TaskFilterContext.Provider
            value={{
                teamIds,
                userIds,
                deviceIds,
                setTeamIds,
                setUserIds,
                setDeviceIds,
                onTeamFilterApplied,
                onUserFilterApplied: setUserIds,
                onDeviceFilterApplied: setDeviceIds,
                selectedTaskId,
                setSelectedTaskId,
                timeType,
                timeRange,
                timezone,
                setTime
            }}
        >
            {children}
        </TaskFilterContext.Provider>
    );
}
